/**
 * Electron shell bridge — auto-record while an incident is active, and feed the
 * shell the recording context the vault backend requires.
 *
 * The Electron recording shell records the operator's window exactly when there
 * is an active incident in ANY community/venue. The web app already tracks that
 * as `venueDataStore.totalIncidentCount` (the sum of `activeIncidents` across
 * every loaded venue, updated live via MQTT). This composable watches that count
 * and, when running INSIDE the Electron shell, signals the shell to:
 *   - start recording on the 0 -> >=1 crossing (an incident became active), and
 *   - stop recording on the >=1 -> 0 crossing (all incidents resolved).
 *
 * It also reports this window's RECORDING CONTEXT to the shell, which the
 * shell's vault client (electron-shell/main.js) needs to talk to the real
 * audit-recording backend:
 *   - accessToken — the operator's consumer JWT (vault authenticates with it);
 *   - communityId — the current community's `uniqueId` UUID. Vault keys
 *     operator grants on this UUID, NOT the numeric community id the frontend
 *     uses in URLs (verified against dev vault 2026-08-03);
 *   - venueIds / incidentIds — loaded venues + live active-incident ids;
 *   - route — the current app route, stamped onto each chunk (X-Route).
 *
 * The shell exposes `window.__electronCaptureBridge` from its preload, so this
 * is a complete no-op in a normal browser (the bridge is absent).
 */
import { computed, watch } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useAuthenticationStore } from "@/stores/authentication";
import { useVenueDataStore } from "@/stores/venueData";
import { EnvironmentService } from "@/utils/environment";

/** Per-window recording context the shell forwards to the vault backend. */
interface ShellRecordingContext {
    accessToken: string | null;
    /** Community `uniqueId` UUID (vault's grant key) — null until resolvable. */
    communityId: string | null;
    venueIds: string[];
    incidentIds: string[];
    route: string;
}

interface ElectronCaptureBridge {
    /**
     * Report the live active-incident state + count. Called on EVERY count
     * change so the shell's panel stays accurate; the shell itself decides
     * recording start/stop from the 0 <-> active crossing.
     */
    setIncidentActive: (active: boolean, count: number) => void;
    /**
     * Report this window's recording context (token + identity). Optional so
     * an older shell binary without the vault integration keeps working.
     */
    setContext?: (ctx: ShellRecordingContext) => void;
}

function getBridge(): ElectronCaptureBridge | null {
    const bridge = (
        window as unknown as { __electronCaptureBridge?: ElectronCaptureBridge }
    ).__electronCaptureBridge;
    return bridge && typeof bridge.setIncidentActive === "function" ? bridge : null;
}

/**
 * Wire active-incident detection to the Electron recording shell. Safe to call
 * unconditionally in any environment: it returns immediately outside the shell.
 */
export function useElectronIncidentBridge(): void {
    const bridge = getBridge();
    if (!bridge) return; // not inside the Electron shell — nothing to do

    const venueDataStore = useVenueDataStore();

    // Report the live active-incident count on every change so the shell's panel
    // stays accurate (e.g. 1 -> 3 as incidents open in other venues). The shell
    // de-dupes to the 0 <-> active crossing for recording start/stop itself.
    // `immediate` so an incident already active at load is reported right away.
    watch(
        () => venueDataStore.totalIncidentCount,
        (count) => bridge.setIncidentActive(count > 0, count),
        { immediate: true },
    );

    // Context channel — only when the shell supports it (vault-integrated build).
    if (typeof bridge.setContext !== "function") return;

    const authenticationStore = useAuthenticationStore();
    const { accessToken, user } = storeToRefs(authenticationStore);
    const route = useRoute();
    // The community subdomain this window is on ("aqsacom" in
    // app.dev.aqsacom.corridorsafety.com); matches Community.orgName.
    const { community: hostCommunity } = EnvironmentService.parseEnvironment();

    const context = computed<ShellRecordingContext>(() => {
        const membership = hostCommunity
            ? user.value?.memberships?.find(
                  (m) => m.community?.orgName === hostCommunity,
              )
            : undefined;
        return {
            accessToken: accessToken.value ?? null,
            communityId: membership?.community?.uniqueId ?? null,
            venueIds: venueDataStore.allVenues.map((v) => String(v.id)),
            incidentIds: venueDataStore.allActiveIncidents.map((i) =>
                String(i.id),
            ),
            route: route.fullPath,
        };
    });

    // The computed re-evaluates (new object identity) whenever any source
    // changes; changes are user/MQTT-paced, so no throttling needed.
    watch(context, (ctx) => bridge.setContext?.(ctx), { immediate: true });
}
